import { useMemo } from 'react'
import { Currency, CurrencyAmount, ETHER, Token, TokenAmount } from '@pancakeswap-libs/sdk'
import JSBI from 'jsbi'
import {
  AggregatedQuoteResultBest,
  NATIVE_TOKEN_SENTINEL,
  QuoteRequest,
  RouteHop,
  getEligibleProviders,
} from '../aggregator'
import useAggregatorQuote from './useAggregatorQuote'
import { useActiveWeb3React } from './index'

/**
 * Bridges the aggregator's plain-string quotes to the SDK currency types the swap
 * screen is built on.
 *
 * The aggregator layer deliberately knows nothing about the SDK - it deals in
 * addresses and base-unit strings - so this is the only place the two meet.
 */

/** True when at least one configured source can quote this chain. */
export function useAggregatorActive(): boolean {
  const { chainId } = useActiveWeb3React()
  return useMemo(() => (chainId ? getEligibleProviders(chainId).length > 0 : false), [chainId])
}

export function currencyToTokenRef(currency: Currency | undefined, chainId: number | undefined) {
  if (!currency) return undefined
  if (currency === ETHER) {
    return { address: NATIVE_TOKEN_SENTINEL, decimals: 18, symbol: 'BNB' }
  }
  if (currency instanceof Token && currency.chainId === chainId) {
    return { address: currency.address, decimals: currency.decimals, symbol: currency.symbol ?? '' }
  }
  return undefined
}

/** Base-unit string back into a CurrencyAmount of the given currency. */
export function toCurrencyAmount(currency: Currency | undefined, raw: string | undefined) {
  if (!currency || !raw) return undefined
  try {
    const amount = JSBI.BigInt(raw)
    return currency === ETHER
      ? CurrencyAmount.ether(amount)
      : new TokenAmount(currency as Token, amount)
  } catch {
    return undefined
  }
}

export interface AggregatorTrade {
  /** Aggregator is serving this chain. When false the caller keeps its own routing. */
  active: boolean
  loading: boolean
  error: string | null
  quote: AggregatedQuoteResultBest | null
  outputAmount: CurrencyAmount | undefined
  /** Worst-case output after slippage - what the user is actually guaranteed. */
  minimumReceived: CurrencyAmount | undefined
  priceImpactBps: number | undefined
  route: RouteHop[]
  /** Per-quote approval target. Not a global constant - see AggregatorQuote. */
  allowanceTarget: string | undefined
  /** Sources that failed this round, for surfacing rather than swallowing. */
  failures: { providerLabel: string; message: string }[]
  refresh: () => void
}

/**
 * Live aggregator quote for an exact-input swap.
 *
 * Exact-output is not supported: the aggregator APIs in use quote sell-amount-in
 * only. Callers must fall back or prompt when the user drives the output field.
 */
export default function useAggregatorTrade(
  inputCurrency: Currency | undefined,
  outputCurrency: Currency | undefined,
  parsedInputAmount: CurrencyAmount | undefined,
  slippageBps: number,
  options: { paused?: boolean } = {}
): AggregatorTrade {
  const { chainId, account } = useActiveWeb3React()
  const active = useAggregatorActive()

  const request: QuoteRequest | null = useMemo(() => {
    if (!active || !chainId) return null

    const sellToken = currencyToTokenRef(inputCurrency, chainId)
    const buyToken = currencyToTokenRef(outputCurrency, chainId)
    if (!sellToken || !buyToken) return null

    // A zero or absent amount is not an error, just nothing to quote yet.
    const sellAmount = parsedInputAmount?.raw?.toString()
    if (!sellAmount || sellAmount === '0') return null

    // Quoting the same token both ways is never a real trade and some sources
    // return a confusing error rather than an empty route.
    if (sellToken.address.toLowerCase() === buyToken.address.toLowerCase()) return null

    return {
      chainId,
      sellToken,
      buyToken,
      sellAmount,
      slippageBps,
      taker: account ?? undefined,
    }
  }, [active, chainId, inputCurrency, outputCurrency, parsedInputAmount, slippageBps, account])

  const { data, loading, error, refresh } = useAggregatorQuote(request, { paused: options.paused })

  return useMemo(() => {
    const best = data?.best ?? null
    return {
      active,
      loading,
      error,
      quote: best,
      outputAmount: toCurrencyAmount(outputCurrency, best?.buyAmount),
      minimumReceived: toCurrencyAmount(outputCurrency, best?.minBuyAmount),
      priceImpactBps: best?.priceImpactBps,
      route: best?.route ?? [],
      allowanceTarget: best?.allowanceTarget,
      failures: (data?.failures ?? []).map((f) => ({ providerLabel: f.providerLabel, message: f.message })),
      refresh,
    }
  }, [active, loading, error, data, outputCurrency, refresh])
}
